/**
 * App Layout
 *
 * Main application layout component with sidebar, content area, and footer.
 * Provides consistent structure across all pages.
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Sidebar } from './Sidebar';
import { ImpersonateBanner } from './ImpersonateBanner';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * AppLayout component
 * Wraps page content with sidebar and footer
 */
export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
      />

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          marginLeft: { xs: 0, md: '240px' }, // Account for fixed sidebar on desktop
        }}
      >
        {/* Mobile Hamburger Menu Button */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            display: { md: 'none' },
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 1200,
            bgcolor: 'primary.main',
            color: '#ffffff',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Impersonation banner — shown when a SUPER_USER is impersonating another user */}
        <ImpersonateBanner />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            width: '100%',
            px: { xs: 2, sm: 3 },
            py: { xs: 6, sm: 5 },
          }}
        >
          {children}
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            bgcolor: 'primary.dark',
            borderTop: '1px solid rgba(204, 204, 204, 0.5)',
            p: 2.5,
            textAlign: 'center',
            mt: 'auto',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            © {new Date().getFullYear()} V2Solutions Inc. All Rights Reserved
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
